import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Phone } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { RelatedLinks } from "@/components/RelatedLinks";
import { AdSlot } from "@/components/AdSlot";
import { leasingFaq, leasingSections, leasingTldr } from "@/data/leasing-guide";

const URL = "https://www.akhusbilfritid.se/leasa-husbil";

export const Route = createFileRoute("/leasa-husbil")({
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(leasingFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Leasa husbil 2026 – går det, vad kostar det & momsen" },
      {
        name: "description",
        content:
          "Privatleasing av husbil finns nästan inte i Sverige. Vad som faktiskt erbjuds, riktiga månadskostnader, momsavdraget för företag och avtalsraderna som kostar.",
      },
      {
        name: "keywords",
        content:
          "leasa husbil, privatleasing husbil, husbil leasing företag, leasa husbil pris, finansiell leasing husbil, husbil avbetalning, husbil moms leasing",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Leasa husbil – vad som gäller, vad det kostar" },
      {
        property: "og:description",
        content:
          "Operationell mot finansiell leasing, avbetalning, momsavdrag och förmånsbeskattning — med räkneexempel från våra egna affärer i Kalix.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: "Leasa husbil — vad som faktiskt erbjuds i Sverige och vad det kostar",
          url: URL,
          inLanguage: "sv-SE",
          about: "Leasing och finansiering av husbil i Sverige",
          author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          publisher: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Leasa husbil", item: URL },
          ],
        }),
      },
    ],
  }),
  component: LeasaHusbilPage,
});

const facts = [
  { k: "≈ 11 300 kr", v: "per månad, husbil 1 250 000 kr, 20 % kontant, 120 mån" },
  { k: "50 %", v: "momsavdrag på leasingavgift för personbilsklassad husbil" },
  { k: "20 %", v: "kontantinsats finansbolagen normalt kräver på fritidsfordon" },
  { k: "3 500–6 000 kr", v: "driftkostnad per månad utöver finansieringen" },
];

export default function LeasaHusbilPage() {
  return (
    <>
      <PageHeader
        label="Nationell guide · Ekonomi"
        title={
          <>
            Leasa husbil — <span className="font-italic-display text-accent">vad som faktiskt gäller</span>.
          </>
        }
        sub="Privatleasing av husbil finns nästan inte i Sverige. Här är vad som erbjuds i stället, riktiga månadskostnader, företagens momsavdrag och de avtalsrader vi alltid läser åt kunden."
      />

      <section className="container-edge pt-14 md:pt-20">
        <div className="mb-8 rounded-2xl border border-accent/30 bg-accent/5 p-6">
          <p className="eyebrow">Kort svar</p>
          <p className="mt-2 text-base leading-relaxed">
            Privatleasing av husbil är ovanligt i Sverige. För privatköpare är avbetalning oftast det realistiska alternativet;
            företag kan jämföra finansiell leasing, moms och restvärde. Räkna alltid på total kostnad, inte bara månadsbeloppet.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{f.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial
          eyebrow="Guiden"
          sections={leasingSections}
          faq={leasingFaq}
          tldr={leasingTldr}
          author="AK Husbil & Fritid, Kalix"
          updated="augusti 2026"
        />
      </div>

      <div className="container-edge">
        <AdSlot variant="in-article" />
      </div>

      <section className="container-edge py-14 md:py-20">
        <div className="rounded-3xl border border-border bg-foreground p-8 text-background md:p-14">
          <p className="eyebrow" style={{ color: "hsl(var(--accent))" }}>
            Räkna på din situation
          </p>
          <h2 className="mt-3 font-display text-4xl leading-[1.05] md:text-5xl">
            Vi lägger leasing och avbetalning sida vid sida.
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed opacity-80">
            Ta med tänkt användning i veckor per år — och bokslutet om det gäller företag. Vi räknar efter moms och
            förmån, och säger till om du hellre bör hyra.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="tel:0708629743"
              className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3.5 text-sm font-semibold text-accent-foreground hover:opacity-90"
            >
              <Phone size={16} /> 070-862 97 43
            </a>
            <Link
              to="/verktyg/manadskostnad-husbil"
              className="inline-flex items-center gap-2 rounded-full border border-background/30 px-6 py-3.5 text-sm font-semibold hover:bg-background/10"
            >
              Kalkylator för månadskostnad <ArrowUpRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <div className="container-edge pb-4">
        <RelatedLinks
          keyId="leasa-husbil"
          text="leasa husbil leasing finansiering avbetalning ränta månadskostnad restvärde avskrivning moms företag köpa husbil"
          currentHref="/leasa-husbil"
        />
      </div>

      <div className="container-edge pb-20">
        <AdSlot variant="multiplex" label="Relaterat innehåll" />
      </div>
    </>
  );
}
